import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  CircleHelp,
  X,
  ChevronRight,
  ChevronLeft,
  MousePointer2,
  Hand,
  Plus,
  Search,
  Maximize,
  Tags,
  Activity,
  Waypoints,
  Minus,
  Sparkles,
  GripHorizontal,
  Check,
  Circle,
  ShieldOff,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// VN reskin notes (GraphTutorial is fully inline-styled — CSS vars unavailable):
//
// All resolved hex/rgba values map to VN semantic tokens as follows:
//
// overlayBg     mainBg dark #0E131C / light #d5dde8
// cardBg        surface dark #121C2E / light #e4eaf2
// cardBorder    surfaceBorder dark #2A3C50 / light #a0afc2
// cardShadow    shadow-login-panel equivalent
// textPrimary   mainText resolved
// textSecondary mutedText resolved
// closeBtn bg   innerBg resolved / mainButtonBg/50 on hover
// task undone   innerBg resolved bg + surfaceBorder resolved border
// footer btns   surfaceBorder resolved borders
// next disabled mainButtonBg at 30% opacity
// progress track surfaceBorder resolved (quieter than blue velBlue track)
// step dots     surfaceBorder resolved (unvisited)
// TutorialButton hover mainButtonBg/50 + surfaceBorder border
//
// Unchanged (intentional semantic/categorical):
//   cat.color values — tutorial category palette, not UI chrome
//   activeCase/successDot green tones
//   Sandbox banner amber — intentional warning indicator
//   Spotlight SVG feedback colors — tutorial-specific
//
// Border radii:
//   card 14 → 8 (rounded-lg)
//   icon container 10 → 8 (rounded-lg)
//   footer buttons 7 → 8 (rounded-lg)
//   category badge 5 → 9999 (pill → rounded-full)
//   sandbox banner 20 → 9999 (pill banner → rounded-full)
//   task checkbox 5 — kept (18×18 tiny checkbox, not a structural card)
// ═══════════════════════════════════════════════════════════════════════════════

const AUTO_ADVANCE_MS = 800;

// ── Step definitions (unchanged) ─────────────────────────────────────────────
const STEPS = [
  {
    id: "welcome",
    title: "Welcome to Your Graph",
    body: "This guided tour spotlights real controls and waits for you to try them.\n\nYou're in sandbox mode — everything you do during the tutorial (creating nodes, edges, etc.) is temporary and won't be saved. Explore freely!",
    icon: Sparkles,
    anchor: "center",
    category: "intro",
    skipCheck: true,
  },
  {
    id: "navigate",
    title: "Navigate the Canvas",
    body: "Try panning and zooming the graph canvas. Any scroll or drag will do.",
    icon: Hand,
    anchor: "graph-canvas",
    position: "aside",
    category: "basics",
    interactive: true,
    prompt: "Scroll to zoom or drag to pan",
    tasks: [
      {
        id: "zoom-or-pan",
        label: "Zoom or pan the canvas",
        check: (gs, init) => gs.zoomLevel !== init.zoomLevel,
      },
    ],
  },
  {
    id: "node-hover",
    title: "Hover Over a Node",
    body: "Move your cursor over any node. It will highlight and action icons appear.",
    icon: MousePointer2,
    anchor: "graph-canvas",
    position: "aside",
    category: "basics",
    interactive: true,
    prompt: "Hover over a node",
    tasks: [
      {
        id: "hover",
        label: "Hover over any node",
        check: (gs) => gs.hoveredNode != null,
      },
    ],
  },
  {
    id: "create-node",
    title: "Create a Standalone Node",
    body: "Double-click on empty canvas space, fill in the form, then submit it to create the node.",
    icon: Plus,
    anchor: "graph-canvas",
    position: "aside",
    category: "creating",
    interactive: true,
    prompt: "Double-click empty space, fill the form, and submit",
    tasks: [
      {
        id: "create-standalone",
        label: "Create a new node (complete the form)",
        check: (gs, init) => gs.nodeCount > init.nodeCount,
      },
    ],
  },
  {
    id: "connect-drag",
    title: "Connect Two Nodes",
    body: "Hover a node, then drag from its + icon. Drop onto another node or onto empty space to create a new connected node. Complete the form to finish.",
    icon: Plus,
    anchor: "graph-canvas",
    position: "aside",
    category: "creating",
    interactive: true,
    prompt: "Drag from a + icon and complete the connection",
    tasks: [
      {
        id: "new-connection",
        label: "Create a new connection (complete the form)",
        check: (gs, init) => gs.linkCount > init.linkCount,
      },
    ],
  },
  {
    id: "multi-select",
    title: "Multi-Select Nodes",
    body: "Hold Shift and drag on empty space to draw a selection box. Selected nodes show dashed rings.",
    icon: GripHorizontal,
    anchor: "graph-canvas",
    position: "aside",
    category: "advanced",
    interactive: true,
    prompt: "Hold Shift + drag to box-select",
    tasks: [
      {
        id: "box-select",
        label: "Select multiple nodes with Shift+drag",
        check: (gs) => gs.selectedNodeIds?.size > 0,
      },
    ],
  },
  {
    id: "graph-source",
    title: "Switch Graph Source",
    body: "Click this picker to switch between your personal graph, team members' graphs, or the global view.",
    icon: Waypoints,
    anchor: "graph-source",
    position: "top",
    category: "controls",
    dropUp: 400,
    prompt: "Click the source picker and select an option",
    tasks: [
      {
        id: "open-source",
        label: "Open the graph source picker",
        check: (gs) => gs.graphSourceOpen,
      },
    ],
  },
  {
    id: "search",
    title: "Search Your Graph",
    body: "Type at least 2 characters to search. Matching nodes glow on the canvas.",
    icon: Search,
    anchor: "search-bar",
    position: "top",
    category: "controls",
    prompt: "Type something in the search bar",
    tasks: [
      {
        id: "type-search",
        label: "Type a search query (2+ characters)",
        check: (gs) => gs.barSearch?.length >= 2,
      },
    ],
  },
  {
    id: "action-buttons",
    title: "View Controls",
    body: "These toggle different view modes. Try toggling any of them.",
    icon: Maximize,
    anchor: "action-buttons",
    position: "top",
    category: "controls",
    prompt: "Click any toolbar button",
    tasks: [
      {
        id: "toggle-any",
        label: "Toggle a view control",
        check: (gs, init) =>
          gs.showEdgeLabels !== init.showEdgeLabels ||
          gs.manualOnly !== init.manualOnly ||
          gs.hideOrphans !== init.hideOrphans,
      },
    ],
  },
  {
    id: "degree-slider",
    title: "Hub Highlight",
    body: "Drag the slider to highlight highly-connected hub nodes.",
    icon: Activity,
    anchor: "degree-slider",
    position: "top",
    category: "controls",
    prompt: "Drag the slider",
    tasks: [
      {
        id: "slide",
        label: "Move the degree slider",
        check: (gs, init) => gs.degreeHighlight !== init.degreeHighlight,
      },
    ],
  },
  {
    id: "limit-control",
    title: "Result Limit",
    body: "Click + or − to change how many documents are fetched.",
    icon: Minus,
    anchor: "limit-control",
    position: "top",
    category: "controls",
    prompt: "Click + or − to adjust",
    tasks: [
      {
        id: "change-limit",
        label: "Change the result limit",
        check: (gs, init) => gs.graphLimit !== init.graphLimit,
      },
    ],
  },
  {
    id: "type-pills",
    title: "Filter by Type",
    body: "Click any colored pill to isolate that node type. Click again to remove the filter.",
    icon: Tags,
    anchor: "type-pills",
    position: "top",
    category: "controls",
    prompt: "Click a type pill to filter",
    tasks: [
      {
        id: "click-pill",
        label: "Toggle a type filter",
        check: (gs) => gs.activeTypeFilter?.size > 0,
      },
    ],
  },
  {
    id: "done",
    title: "You're All Set!",
    body: "You've completed the tour! Everything you did was in sandbox mode — nothing was saved and your graph has been restored. Replay anytime from the ? button.",
    icon: Sparkles,
    anchor: "center",
    category: "intro",
    skipCheck: true,
  },
];

const CATEGORIES = {
  intro: { label: "Getting Started", color: "#0ea5e9" },
  basics: { label: "Canvas Basics", color: "#22c55e" },
  creating: { label: "Create & Connect", color: "#a855f7" },
  advanced: { label: "Power Features", color: "#f59e0b" },
  controls: { label: "Controls", color: "#f43f5e" },
};

// ── Spotlight rect (unchanged) ────────────────────────────────────────────────
function getSpotlightRect(anchor, dropUp) {
  if (anchor === "center") return null;
  const el = document.querySelector(`[data-tutorial="${anchor}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const pad = 10;
  if (dropUp) {
    const top = Math.max(0, r.top - dropUp);
    return {
      x: r.left - pad,
      y: top,
      w: Math.max(r.width + pad * 2, 300),
      h: r.bottom + pad - top,
      el,
      buttonY: r.top,
      buttonH: r.height,
    };
  }
  return {
    x: r.left - pad,
    y: r.top - pad,
    w: r.width + pad * 2,
    h: r.height + pad * 2,
    el,
  };
}

// ── Tooltip positioning (unchanged) ──────────────────────────────────────────
function getTooltipStyle(rect, position, tipW, tipH) {
  if (!rect)
    return {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  const gap = 14;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = rect.x + rect.w / 2;
  if (position === "aside")
    return { position: "fixed", top: 76, right: gap, left: "auto" };
  let top = rect.y - tipH - gap;
  let left = Math.max(gap, Math.min(cx - tipW / 2, vw - tipW - gap));
  if (top < 60) top = rect.y + rect.h + gap;
  top = Math.max(60, Math.min(top, vh - tipH - gap));
  return { position: "fixed", top, left };
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const GraphTutorial = ({
  dark = true,
  onClose,
  onStepChange,
  graphState = {},
}) => {
  const [step, setStep] = useState(0);
  const [animClass, setAnimClass] = useState("in");
  const [spotRect, setSpotRect] = useState(null);
  const [tipPos, setTipPos] = useState({});
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [stepCompleted, setStepCompleted] = useState(false);
  const tipRef = useRef(null);
  const initialStateRef = useRef({ ...graphState });
  const autoAdvanceTimerRef = useRef(null);

  const current = STEPS[step];
  const cat = CATEGORIES[current.category];
  const progress = ((step + 1) / STEPS.length) * 100;
  const isCenter = current.anchor === "center";
  const isInteractive = current.interactive;

  // ── VN resolved theme values ─────────────────────────────────────────────
  // Overlay: mainBg resolved at 72% — the blue undertone reads through slightly
  const overlayBg = dark ? "rgba(14,19,28,0.72)" : "rgba(213,221,232,0.68)";

  // Card surface: surface token resolved at ~97%
  const cardBg = dark ? "rgba(18,28,46,0.97)" : "rgba(228,234,242,0.98)";

  // Card border: surfaceBorder resolved
  const cardBorder = dark ? "rgba(42,60,80,0.90)" : "rgba(160,175,194,0.80)";

  // shadow-login-panel equivalent (0 12px 50px rgba(0,0,0,...))
  const cardShadow = dark
    ? "0 12px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(42,60,80,0.50)"
    : "0 12px 50px rgba(0,0,0,0.18), 0 0 0 1px rgba(160,175,194,0.40)";

  // Text: mainText / mutedText resolved
  const textPrimary = dark ? "rgba(225,232,240,0.92)" : "rgba(15,23,42,0.90)";
  const textSecondary = dark
    ? "rgba(148,163,184,0.75)"
    : "rgba(100,116,139,0.78)";
  const textMuted = dark ? "rgba(148,163,184,0.50)" : "rgba(100,116,139,0.50)";

  // innerBg resolved — for task items, close button, etc.
  const innerBg = dark ? "rgba(16,24,40,0.40)" : "rgba(213,221,232,0.50)";
  // mainButtonBg/50 resolved — for hover states
  const hoverBg = dark ? "rgba(30,50,72,0.50)" : "rgba(192,203,218,0.50)";
  // surfaceBorder resolved — for borders
  const borderColor = dark ? "rgba(42,60,80,0.60)" : "rgba(160,175,194,0.55)";
  const borderHover = dark ? "rgba(42,60,80,0.90)" : "rgba(160,175,194,0.80)";
  // mainButtonBg at 30% for disabled next button
  const disabledBg = dark ? "rgba(30,50,72,0.30)" : "rgba(192,203,218,0.30)";
  // surfaceBorder for progress track + unvisited dots
  const trackBg = dark ? "rgba(42,60,80,0.50)" : "rgba(160,175,194,0.30)";

  // ── Measure ───────────────────────────────────────────────────────────────
  const measure = useCallback(() => {
    const rect = getSpotlightRect(current.anchor, current.dropUp);
    setSpotRect(rect);
    const tipEl = tipRef.current;
    const tipW = tipEl?.offsetWidth || 360;
    const tipH = tipEl?.offsetHeight || 260;
    setTipPos(getTooltipStyle(rect, current.position, tipW, tipH));
  }, [current]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const iv = setInterval(measure, 400);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      clearInterval(iv);
    };
  }, [measure]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const goTo = useCallback(
    (target) => {
      if (target === step || target < 0 || target >= STEPS.length) return;
      setAnimClass("out");
      setTimeout(() => {
        setStep(target);
        setAnimClass("in");
      }, 160);
    },
    [step],
  );

  const next = useCallback(() => {
    if (step < STEPS.length - 1) goTo(step + 1);
    else onClose?.();
  }, [step, goTo, onClose]);

  const prev = useCallback(() => {
    if (step > 0) goTo(step - 1);
  }, [step, goTo]);

  // ── Task completion + auto-advance ────────────────────────────────────────
  useEffect(() => {
    if (current.skipCheck || !current.tasks) {
      setStepCompleted(true);
      return;
    }
    const newCompleted = new Set();
    for (const task of current.tasks) {
      if (task.check(graphState, initialStateRef.current))
        newCompleted.add(task.id);
    }
    setCompletedTasks(newCompleted);
    const allDone = current.tasks.every((t) => newCompleted.has(t.id));
    if (allDone && !stepCompleted) {
      setStepCompleted(true);
      if (step < STEPS.length - 1) {
        autoAdvanceTimerRef.current = setTimeout(
          () => goTo(step + 1),
          AUTO_ADVANCE_MS,
        );
      }
    }
    return () => {
      if (autoAdvanceTimerRef.current)
        clearTimeout(autoAdvanceTimerRef.current);
    };
  }, [graphState, current, step, stepCompleted, goTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  useEffect(() => {
    setCompletedTasks(new Set());
    setStepCompleted(current.skipCheck || !current.tasks);
    initialStateRef.current = { ...graphState };
    if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev, onClose]);

  // ── Overlay (4-rect approach, unchanged logic) ────────────────────────────
  const renderOverlay = () => {
    if (isInteractive) return null;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (isCenter || !spotRect) {
      return (
        <div
          className="fixed inset-0 z-[9998]"
          style={{
            background: overlayBg,
            backdropFilter: "blur(2px)",
            transition: "background 0.3s",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        />
      );
    }

    const s = spotRect;
    const r = 12;
    const spotBorderColor = stepCompleted ? "#22c55e90" : cat.color + "70";
    const borderRect =
      current.dropUp && s.buttonY != null
        ? { x: s.x, y: s.buttonY - 10, w: s.w, h: s.buttonH + 20 }
        : s;

    return (
      <>
        <div
          className="fixed z-[9998]"
          style={{
            top: 0,
            left: 0,
            width: vw,
            height: Math.max(0, s.y),
            background: overlayBg,
            pointerEvents: "auto",
          }}
        />
        <div
          className="fixed z-[9998]"
          style={{
            top: s.y + s.h,
            left: 0,
            width: vw,
            height: Math.max(0, vh - s.y - s.h),
            background: overlayBg,
            pointerEvents: "auto",
          }}
        />
        <div
          className="fixed z-[9998]"
          style={{
            top: s.y,
            left: 0,
            width: Math.max(0, s.x),
            height: s.h,
            background: overlayBg,
            pointerEvents: "auto",
          }}
        />
        <div
          className="fixed z-[9998]"
          style={{
            top: s.y,
            left: s.x + s.w,
            width: Math.max(0, vw - s.x - s.w),
            height: s.h,
            background: overlayBg,
            pointerEvents: "auto",
          }}
        />

        {/* Spotlight border — marching ants around the target */}
        <svg
          className="fixed z-[9999]"
          style={{
            top: 0,
            left: 0,
            width: vw,
            height: vh,
            pointerEvents: "none",
          }}
        >
          <rect
            x={borderRect.x}
            y={borderRect.y}
            width={borderRect.w}
            height={borderRect.h}
            rx={r}
            ry={r}
            fill="none"
            stroke={spotBorderColor}
            strokeWidth="2"
            strokeDasharray={stepCompleted ? "0" : "6 4"}
          >
            {!stepCompleted && (
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to="20"
                dur="1.5s"
                repeatCount="indefinite"
              />
            )}
          </rect>
          {/* Glow ring */}
          <rect
            x={borderRect.x - 3}
            y={borderRect.y - 3}
            width={borderRect.w + 6}
            height={borderRect.h + 6}
            rx={r + 3}
            ry={r + 3}
            fill="none"
            stroke={(stepCompleted ? "#22c55e" : cat.color) + "18"}
            strokeWidth="4"
          >
            <animate
              attributeName="opacity"
              values="0.7;0.2;0.7"
              dur="2s"
              repeatCount="indefinite"
            />
          </rect>
        </svg>
      </>
    );
  };

  // ── Task list ─────────────────────────────────────────────────────────────
  const renderTasks = () => {
    if (!current.tasks) return null;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
          marginBottom: 12,
        }}
      >
        {current.tasks.map((task) => {
          const done = completedTasks.has(task.id);
          return (
            <div
              key={task.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "7px 10px",
                borderRadius: 8,
                // Done: activeCase green family. Undone: innerBg + surfaceBorder
                background: done
                  ? dark
                    ? "rgba(34,197,94,0.08)"
                    : "rgba(34,197,94,0.06)"
                  : innerBg,
                border: `1px solid ${
                  done
                    ? dark
                      ? "rgba(34,197,94,0.20)"
                      : "rgba(34,197,94,0.15)"
                    : dark
                      ? "rgba(42,60,80,0.50)"
                      : "rgba(160,175,194,0.30)"
                }`,
                transition: "all 0.3s ease",
              }}
            >
              {/* Checkbox — small enough that 5px radius is acceptable */}
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: done ? "#22c55e" : "transparent",
                  border: done
                    ? "none"
                    : `1.5px solid ${dark ? "rgba(42,60,80,0.80)" : "rgba(160,175,194,0.60)"}`,
                  transition: "all 0.25s ease",
                  flexShrink: 0,
                }}
              >
                {done ? (
                  <Check size={11} style={{ color: "white", strokeWidth: 3 }} />
                ) : (
                  <Circle size={7} style={{ color: cat.color, opacity: 0.4 }} />
                )}
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: done ? 500 : 400,
                  color: done
                    ? dark
                      ? "rgba(34,197,94,0.90)"
                      : "rgba(22,163,74,0.90)"
                    : textSecondary,
                  textDecoration: done ? "line-through" : "none",
                  textDecorationColor: done
                    ? "rgba(34,197,94,0.30)"
                    : undefined,
                  transition: "all 0.3s ease",
                }}
              >
                {task.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {renderOverlay()}

      {/* Sandbox banner — pill shape (rounded-full), amber intentional warning color */}
      <div
        className="fixed z-[10001] flex items-center gap-2"
        style={{
          top: 64,
          left: "50%",
          transform: "translateX(-50%)",
          padding: "6px 16px",
          borderRadius: 9999,
          background: dark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.12)",
          border: "1px solid rgba(245,158,11,0.30)",
          backdropFilter: "blur(8px)",
          pointerEvents: "none",
        }}
      >
        <ShieldOff
          size={13}
          style={{ color: "rgba(245,158,11,0.90)", flexShrink: 0 }}
        />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "rgba(245,158,11,0.95)",
            whiteSpace: "nowrap",
          }}
        >
          Sandbox Mode — nothing you do here will be saved
        </span>
      </div>

      {/* ── Tooltip card ─────────────────────────────────────────────────── */}
      <div
        ref={tipRef}
        className="fixed z-[10000]"
        style={{
          ...tipPos,
          width: isCenter
            ? "min(440px, calc(100vw - 40px))"
            : "min(360px, calc(100vw - 40px))",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          opacity: animClass === "in" ? 1 : 0,
          transition:
            "opacity 0.2s ease, top 0.3s ease, left 0.3s ease, right 0.3s ease",
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            borderRadius: 8, // rounded-lg
            boxShadow: cardShadow,
            overflow: "hidden",
          }}
        >
          {/* Progress bar — surfaceBorder track (quieter than velBlue) */}
          <div style={{ height: 3, background: trackBg, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${cat.color}88 0%, ${cat.color} 100%)`,
                transition: "width 0.35s ease",
                borderRadius: 4,
              }}
            />
          </div>

          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 18px 4px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Category badge — rounded-full (pill) */}
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: cat.color,
                  background: cat.color + "15",
                  border: `1px solid ${cat.color}30`,
                  borderRadius: 9999, // rounded-full — it's a pill label
                  padding: "2px 7px",
                }}
              >
                {cat.label}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: textMuted,
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {step + 1} / {STEPS.length}
              </span>
            </div>

            {/* Close button — innerBg bg, hoverBg on hover */}
            <button
              onClick={onClose}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                borderRadius: 8, // rounded-lg
                border: "none",
                background: innerBg,
                cursor: "pointer",
                color: textMuted,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = hoverBg;
                e.currentTarget.style.color = textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = innerBg;
                e.currentTarget.style.color = textMuted;
              }}
              title="Close (Esc)"
            >
              <X size={13} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "10px 18px 14px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              {/* Step icon container — rounded-lg */}
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8, // rounded-lg
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    stepCompleted && !current.skipCheck
                      ? "#22c55e14"
                      : cat.color + "12",
                  border: `1px solid ${stepCompleted && !current.skipCheck ? "#22c55e28" : cat.color + "22"}`,
                  flexShrink: 0,
                  transition: "all 0.3s ease",
                }}
              >
                {stepCompleted && !current.skipCheck ? (
                  <Check size={17} style={{ color: "#22c55e" }} />
                ) : (
                  React.createElement(current.icon, {
                    size: 17,
                    style: { color: cat.color },
                  })
                )}
              </div>
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: textPrimary,
                  lineHeight: 1.3,
                  margin: 0,
                }}
              >
                {current.title}
              </h2>
            </div>

            <p
              style={{
                fontSize: 13,
                lineHeight: 1.6,
                color: textSecondary,
                margin: "0 0 12px",
                whiteSpace: "pre-line",
              }}
            >
              {current.body}
            </p>

            {renderTasks()}

            {/* Action prompt */}
            {current.prompt && !stepCompleted && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: cat.color + "0a",
                  border: `1px dashed ${cat.color}35`,
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: cat.color,
                    boxShadow: `0 0 8px ${cat.color}60`,
                    flexShrink: 0,
                    animation: "tut-pulse 1.5s ease-in-out infinite",
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: cat.color,
                    lineHeight: 1.4,
                  }}
                >
                  {current.prompt}
                </span>
              </div>
            )}

            {/* Completion confirmation */}
            {stepCompleted && !current.skipCheck && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: dark
                    ? "rgba(34,197,94,0.06)"
                    : "rgba(34,197,94,0.05)",
                  border: "1px solid rgba(34,197,94,0.18)",
                }}
              >
                <Check size={13} style={{ color: "#22c55e", flexShrink: 0 }} />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#22c55e",
                    lineHeight: 1.4,
                  }}
                >
                  Done! Moving to next step…
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 18px 14px",
            }}
          >
            {/* Step dot indicators — surfaceBorder for unvisited */}
            <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
              {STEPS.map((_, i) => {
                const c = CATEGORIES[STEPS[i].category];
                return (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    title={STEPS[i].title}
                    style={{
                      width: i === step ? 16 : 5,
                      height: 5,
                      borderRadius: 3,
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      background:
                        i === step
                          ? c.color
                          : i < step
                            ? c.color + "45"
                            : trackBg,
                      transition: "all 0.25s ease",
                    }}
                  />
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {/* Skip */}
              {!stepCompleted && current.tasks && (
                <button
                  onClick={next}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 11,
                    color: textMuted,
                    padding: "6px 8px",
                    textDecoration: "underline",
                    textDecorationColor: "transparent",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecorationColor = textMuted;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecorationColor = "transparent";
                  }}
                >
                  Skip
                </button>
              )}

              {/* Back — ghost button with surfaceBorder border */}
              {step > 0 && (
                <button
                  onClick={prev}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    padding: "6px 12px",
                    borderRadius: 8, // rounded-lg
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: "pointer",
                    background: "transparent",
                    border: `1px solid ${borderColor}`,
                    color: textMuted,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = borderHover;
                    e.currentTarget.style.color = textPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = borderColor;
                    e.currentTarget.style.color = textMuted;
                  }}
                >
                  <ChevronLeft size={13} />
                  Back
                </button>
              )}

              {/* Next / Finish — filled with cat.color gradient when enabled */}
              <button
                onClick={next}
                disabled={!stepCompleted && !current.skipCheck}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  padding: "6px 14px",
                  borderRadius: 8, // rounded-lg
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: stepCompleted ? "pointer" : "not-allowed",
                  background: stepCompleted
                    ? `linear-gradient(135deg, ${cat.color}cc 0%, ${cat.color} 100%)`
                    : disabledBg,
                  border: `1px solid ${stepCompleted ? cat.color + "40" : "transparent"}`,
                  color: stepCompleted ? "rgba(255,255,255,0.95)" : textMuted,
                  boxShadow: stepCompleted
                    ? `0 2px 10px ${cat.color}30`
                    : "none",
                  transition: "all 0.25s ease",
                  opacity: stepCompleted ? 1 : 0.5,
                }}
                onMouseEnter={(e) => {
                  if (stepCompleted) {
                    e.currentTarget.style.boxShadow = `0 4px 16px ${cat.color}45`;
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = stepCompleted
                    ? `0 2px 10px ${cat.color}30`
                    : "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {step === STEPS.length - 1 ? "Finish" : "Next"}
                {step < STEPS.length - 1 && <ChevronRight size={13} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tut-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.8); }
        }
      `}</style>
    </>
  );
};

// ── TutorialButton — VN icon button pattern ───────────────────────────────────
// Default: transparent bg + transparent border
// Hover:   mainButtonBg/50 bg + surfaceBorder border + velBlue glow
// Active:  bg-selectedSection + surfaceBorder border + text-velBlue (if needed)
export const TutorialButton = ({ dark, onClick }) => (
  <button
    title="Interactive tutorial"
    className="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg transition-all duration-200"
    style={{ background: "transparent", border: "1px solid transparent" }}
    onMouseEnter={(e) => {
      // mainButtonBg/50 resolved: dark rgba(30,50,72,0.50) / light rgba(192,203,218,0.50)
      e.currentTarget.style.background = dark
        ? "rgba(30,50,72,0.50)"
        : "rgba(192,203,218,0.50)";
      // surfaceBorder resolved: dark rgba(42,60,80,0.90) / light rgba(160,175,194,0.80)
      e.currentTarget.style.border = dark
        ? "1px solid rgba(42,60,80,0.90)"
        : "1px solid rgba(160,175,194,0.80)";
      // Keep glow — aligned with VN velBlue glow philosophy
      e.currentTarget.style.boxShadow = "0 0 12px rgba(14,165,233,0.12)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.border = "1px solid transparent";
      e.currentTarget.style.boxShadow = "none";
    }}
    onClick={onClick}
  >
    <CircleHelp
      size={16}
      style={{
        color: dark ? "rgba(14,165,233,0.65)" : "rgba(14,165,233,0.70)",
      }}
    />
  </button>
);

export default GraphTutorial;
